import { useEffect, useRef, useState } from "react";

import { useLocalStorage } from "./use-local-storage";

const THEME_STORAGE_KEY = "dark-mode-enabled";

export type ThemeMode =
  | "system"
  | "light"
  | "dark"
  | "accessible-light"
  | "accessible-dark";

// The legacy storage format was boolean | null. Normalize it to ThemeMode.
function resolveStoredMode(
  raw: ThemeMode | boolean | null,
): ThemeMode {
  if (raw === null || raw === undefined) return "system";
  if (raw === true) return "dark"; // legacy migration
  if (raw === false) return "light"; // legacy migration
  return raw;
}

// Hook
export function useDarkMode(): [boolean, React.Dispatch<boolean>] {
  const [themeMode, setThemeMode] = useThemeMode();
  const prefersDarkMode = usePrefersDarkMode();

  const isDark =
    themeMode === "dark" ||
    themeMode === "accessible-dark" ||
    (themeMode === "system" && prefersDarkMode);

  const setEnabled: React.Dispatch<boolean> = (
    value: boolean | ((prev: boolean) => boolean),
  ) => {
    const resolved =
      value instanceof Function ? value(isDark) : value;
    setThemeMode(resolved ? "dark" : "light");
  };

  return [isDark, setEnabled];
}

export function useThemeMode(): [
  ThemeMode,
  React.Dispatch<ThemeMode>,
] {
  const [storedMode, setStoredModeRaw] = useLocalStorage<
    ThemeMode | boolean | null
  >(THEME_STORAGE_KEY, null);

  const mode = resolveStoredMode(storedMode);

  // useLocalStorage's setter closes over storedValue, so it gets a new
  // identity every render. Indirecting through a ref breaks that closure
  // dependency, keeping setMode referentially stable — safe to include
  // in effect dependency arrays without re-firing.
  const setEnabledRef = useRef(setStoredModeRaw);
  useEffect(() => {
    setEnabledRef.current = setStoredModeRaw;
  });

  const setMode: React.Dispatch<ThemeMode> = (value: ThemeMode) => {
    setEnabledRef.current(value === "system" ? null : value);
  };

  return [mode, setMode];
}

// Returns the effective theme after resolving "system" against the OS preference.
// Use this when you need to apply CSS classes to the DOM.
export function useResolvedTheme(): ThemeMode {
  const [themeMode] = useThemeMode();
  const prefersDarkMode = usePrefersDarkMode();

  if (themeMode === "system") {
    return prefersDarkMode ? "dark" : "light";
  }
  return themeMode;
}

// Detect dark mode preference via matchMedia.
function usePrefersDarkMode() {
  const [prefersDarkMode, setPrefersDarkMode] = useState(() => {
    if (typeof window === "undefined") {
      return false;
    }
    return window.matchMedia("(prefers-color-scheme: dark)").matches;
  });

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const mediaQueryList = window.matchMedia(
      "(prefers-color-scheme: dark)",
    );

    const handleChange = (event: MediaQueryListEvent) => {
      setPrefersDarkMode(event.matches);
    };

    if (mediaQueryList.addEventListener) {
      mediaQueryList.addEventListener("change", handleChange);
    } else if (mediaQueryList.addListener) {
      mediaQueryList.addListener(handleChange);
    }

    return () => {
      if (mediaQueryList.removeEventListener) {
        mediaQueryList.removeEventListener("change", handleChange);
      } else if (mediaQueryList.removeListener) {
        mediaQueryList.removeListener(handleChange);
      }
    };
  }, []);

  return prefersDarkMode;
}
